/**
 * Redux middleware for performance monitoring and optimization
 */

import { debounce } from '../../utils/performance';

// Performance monitoring middleware
export const performanceMiddleware = (_store) => (next) => (action) => {
  const start = performance.now();
  
  // Execute the action
  const result = next(action);
  
  const end = performance.now();
  const duration = end - start;
  
  // Log slow actions in development
  if (import.meta.env.DEV && duration > 10) {
    console.warn(`Slow Redux action: ${action.type} took ${duration.toFixed(2)}ms`);
  }
  
  return result;
};

// Debounced action middleware to prevent excessive dispatching
const debouncedActions = new Map();

export const debounceMiddleware = (_store) => (next) => (action) => {
  // Actions that should be debounced
  const debounceConfig = {
    'profile/updateField': 300,
    'sharing/searchUsers': 300,
  };
  
  const debounceTime = debounceConfig[action.type];
  
  if (debounceTime) {
    const key = `${action.type}_${JSON.stringify(action.payload)}`;
    
    if (!debouncedActions.has(key)) {
      const debouncedDispatch = debounce(() => {
        next(action);
        debouncedActions.delete(key);
      }, debounceTime);
      
      debouncedActions.set(key, debouncedDispatch);
    }
    
    debouncedActions.get(key)();
    return action;
  }
  
  return next(action);
};

// Selective update middleware to prevent unnecessary re-renders
export const selectiveUpdateMiddleware = (store) => (next) => (action) => {
  const prevState = store.getState();
  const result = next(action);
  const nextState = store.getState();
  
  // Track state changes for performance analysis
  if (import.meta.env.DEV) {
    const changedSlices = Object.keys(nextState).filter(
      key => prevState[key] !== nextState[key]
    );
    
    if (changedSlices.length > 3) {
      console.warn(`Action ${action.type} changed multiple slices:`, changedSlices);
    }
  }
  
  return result;
};